import React from 'react';

import { Shield } from 'lucide-react';

import { JourneyExplorer } from './JourneyExplorer';
import { JourneyMapNode } from './JourneyMapNode';

import { IMcsaWorld, TMcsaWorldKey } from '../../../../types/mcsa.interface';
import { getExplorerPoint, getWorldStatus } from '../../../../helpers/mcsa-journey.helper';
import { MCSA_MAP_BASE_PATH, MCSA_MAP_VIEW_BOX, MCSA_WORLD_LAYOUT } from '../../../../helpers/mcsa-journey.layout';
import { MCSA_JOURNEY_COPY } from '../../../../helpers/mcsa-journey.copy';

interface JourneyMapProps {
    worlds: IMcsaWorld[];
    completedKeys: TMcsaWorldKey[];
    currentWorld: IMcsaWorld | null;
    onSelectWorld: (world: IMcsaWorld) => void;
}

export const JourneyMap = (props: JourneyMapProps) => {
    const { worlds, completedKeys, currentWorld, onSelectWorld } = props;
    const explorer = getExplorerPoint(worlds, completedKeys);

    return (
        <div className='mcsa_journey_map'>
            <svg className='mcsa_journey_map__motif' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice' aria-hidden='true'>
                <g stroke='#B284FF' strokeWidth='1.3' fill='none' opacity='.28'>
                    <path d='M-20 120 H 210 V 250 H 360' />
                    <path d='M1650 200 H 1420 V 340 H 1250' />
                    <path d='M-20 760 H 180 V 640 H 300' />
                    <path d='M1650 700 H 1460 V 560 H 1330' />
                </g>
                <g fill='#B284FF' opacity='.3'>
                    <circle cx='210' cy='120' r='4' />
                    <circle cx='360' cy='250' r='4' />
                    <circle cx='1420' cy='200' r='4' />
                    <circle cx='1250' cy='340' r='4' />
                    <circle cx='180' cy='760' r='4' />
                    <circle cx='300' cy='640' r='4' />
                    <circle cx='1460' cy='700' r='4' />
                    <circle cx='1330' cy='560' r='4' />
                </g>
            </svg>

            <div className='mcsa_journey_map__float mcsa_journey_map__float--brain' aria-hidden='true'>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M12 5a7 7 0 1 1-7 7' />
                    <path d='M9 9a3 3 0 1 1 6 0' />
                    <circle cx='12' cy='13' r='1' />
                    <path d='M12 16v3' />
                    <circle cx='5' cy='8' r='1' />
                    <circle cx='19' cy='8' r='1' />
                </svg>
            </div>
            <div className='mcsa_journey_map__float mcsa_journey_map__float--shield' aria-hidden='true'>
                <Shield />
            </div>

            <div className='mcsa_journey_map__header'>
                <p className='text-sm text-semibold text-uppercase color--secondary mcsa_journey_map__eyebrow'>
                    {MCSA_JOURNEY_COPY.eyebrow}
                </p>
                <h1 className='text-light color--primary mcsa_journey_map__headline'>{MCSA_JOURNEY_COPY.headline}</h1>
                <p className='color--txt-tertiary mcsa_journey_map__body'>
                    {MCSA_JOURNEY_COPY.body} <b>{MCSA_JOURNEY_COPY.ready}</b>
                </p>
            </div>

            <div className='mcsa_journey_map__stage'>
                <svg className='mcsa_journey_map__path' viewBox={MCSA_MAP_VIEW_BOX} preserveAspectRatio='xMidYMid meet' aria-hidden='true'>
                    <path className='mcsa_journey_map__path__base' d={MCSA_MAP_BASE_PATH} />
                    {worlds.map(world => (
                        <path
                            key={world.key}
                            className={`mcsa_journey_map__path__segment mcsa_journey_map__path__segment--${world.key} ${completedKeys.includes(world.key) ? 'mcsa_journey_map__path__segment--done' : ''}`}
                            d={MCSA_WORLD_LAYOUT[world.key].path}
                        />
                    ))}
                    <circle className='mcsa_journey_map__path__start' cx='64' cy='738' r='9' />
                    <text className='mcsa_journey_map__path__start_label' x='64' y='778' textAnchor='middle'>
                        {MCSA_JOURNEY_COPY.start_label}
                    </text>
                </svg>

                <JourneyExplorer point={explorer} />

                {worlds.map(world => (
                    <JourneyMapNode
                        key={world.key}
                        world={world}
                        status={getWorldStatus(worlds, completedKeys, world.key)}
                        onSelect={onSelectWorld}
                    />
                ))}
            </div>

            {currentWorld && (
                <p className='mcsa_journey__nudge'>
                    <span aria-hidden='true'>👆 </span>
                    {MCSA_JOURNEY_COPY.nudge_before}<b>{currentWorld.map_label}</b>{MCSA_JOURNEY_COPY.nudge_after}
                </p>
            )}
        </div>
    );
};
